import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import DocumentMeta from "react-document-meta";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { useIsPro } from "../imports";
import Layout from "../Layout";
import { H2RHeading } from "../imports";
import { cuesApi } from "../imports";
import { H2RButton } from "../imports";
import { useDynamicValue } from "../imports";
import { socketActions } from "../imports";
import { faClose, faExternalLink, faFileCsv, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

export default function Variables() {
	let { projectId } = useParams();
	const isPro = useIsPro();

	const selectedProject = useSelector(
		(state) => state.main.projects?.[projectId]
	);

	const dynamicLists =
		useSelector((state) => state.main?.projects?.[projectId]?.dynamicLists) ||
		[];

	const meta = {
		title: `Data - ${selectedProject?.name}`,
	};

	if (selectedProject === undefined) {
		return null;
	} else {
		return (
			<DocumentMeta {...meta}>
				<Layout>
					<section className={`mx-5 my-5 flex flex-col flex-1 overflow-auto`}>
						<div className="flex flex-col space-y-3 mx-3">
							<div className="flex justify-between">
								<H2RHeading type="h5">Text variables</H2RHeading>
								<H2RButton
									text="How this works"
									size="small"
									colour="light"
									icon={faExternalLink}
									onClick={() => {
										return socketActions.visitExternal(
											"https://h2r.graphics/docs/control/variables/"
										);
									}}
								/>
							</div>
							<div className="flex flex-wrap gap-3">
								<DynamicValueWrapped id={"text.1"} editable={true} />
								<DynamicValueWrapped id={"text.2"} editable={true} />
								<DynamicValueWrapped id={"text.3"} editable={true} />
								<DynamicValueWrapped id={"text.4"} editable={true} />
								<DynamicValueWrapped id={"text.5"} editable={true} />
								<DynamicValueWrapped id={"text.6"} editable={true} />
							</div>
							<div className="py-2 border-b border-gray-800"></div>
							{dynamicLists.map((list, i) => {
								return (
									<div key={`dynamic_list${i}_list`} className="py-10">
										<DynamicList list={list} listIndex={i} />
									</div>
								);
							})}
							<div className="py-2 border-b border-gray-800"></div>
							{dynamicLists.length < 1 && (
								<>
									<span className="text-sm text-gray-600 italic">
										Add a variable list to get started.
									</span>
								</>
							)}
							{(dynamicLists.length < 1 || isPro) && (
								<H2RButton
									text="Add variable list"
									size="small"
									css="w-min"
									icon={faPlus}
									colour="dark"
									onClick={() => {
										return cuesApi.addDynamicList(projectId);
									}}
								/>
							)}
						</div>
					</section>
				</Layout>
			</DocumentMeta>
		);
	}
}

const DynamicValueWrapped = ({ id, editable = false }) => {
	return (
		<span
			key={`dynamic_value_${id}`}
			className={[
				"px-2 py-1  rounded flex space-x-2 items-center",
				editable && "bg-gray-800",
			].join(" ")}
		>
			<span className="font-bold text-sm select-text text-main-500 font-mono">
				[{id}]
			</span>
			<DynamicValue id={id} editable={editable} />
		</span>
	);
};

export const DynamicValue = ({ id, editable = false }) => {
	let { projectId } = useParams();
	const [value] = useDynamicValue({ id });
	const [v, setV] = useState(value);

	useEffect(() => {
		setV(value);
	}, [value]);

	if (editable) {
		return (
			<input
				className={`bg-transparent rounded outline-none w-20`}
				placeholder="Enter text"
				value={v || ""}
				onChange={(e) => {
					setV(e.target.value);
					return cuesApi.updateDynamicText(
						projectId,
						id,
						e.target.value
					);
				}}
			/>
		);
	}

	if (value === "" || !value) {
		return (
			<span key={`dynamic_value_not_set_${id}`} className="text-gray-500">
				Not set
			</span>
		);
	} else {
		return <span key={`dynamic_value_set_${value}`}>{value}</span>;
	}
};

const DynamicList = ({ list, listIndex }) => {
	const dynamicListMemo = useMemo(
		() => <DynamicListInternals list={list} listIndex={listIndex} />,
		[list]
	);
	return dynamicListMemo;
};

const DynamicListInternals = ({ list, listIndex }) => {
	let { projectId } = useParams();
	const [deleteList, setDeleteList] = useState()

	const selectedRow = list?.[0]?.[0]?.selected || 0;

	const onDragEnd = (result) => {
		const { destination, source } = result;

		if (!destination) return null;

		if (
			destination.droppableId === source.droppableId &&
			destination.index === source.index
		)
			return null;

		if (destination.index === 0) return null;
		cuesApi.reorderListRow(
			projectId,
			listIndex,
			source.index,
			destination.index
		);
	};

	return (
		<>
			<div
				style={{
					gridTemplateColumns: "5rem 1fr 1fr 1fr 3rem",
				}}
				className="grid space-x-2 pb-4"
			>
				<H2RHeading type="h5">List {listIndex + 1}</H2RHeading>
				<DynamicValueWrapped id={`list${listIndex + 1}.${1}`} />
				<DynamicValueWrapped id={`list${listIndex + 1}.${2}`} />
				<DynamicValueWrapped id={`list${listIndex + 1}.${3}`} />
			</div>
			<DragDropContext
				onDragEnd={(result) => {
					onDragEnd(result);
				}}
			>
				<Droppable droppableId={`droppable_list_index_${listIndex}`}>
					{(provided) => (
						<div
							className="grid"
							ref={provided.innerRef}
							{...provided.droppableProps}
						>
							{list?.map((row, listRowIndex) => {
								return (
									<Draggable
										draggableId={`droppable_list_index_${listRowIndex}`}
										index={listRowIndex}
										isDragDisabled={listRowIndex === 0}
										key={`dynamic_list_parent_${listRowIndex}_${listIndex}`}
									>
										{(provided) => (
											<div
												{...provided.draggableProps}
												{...provided.dragHandleProps}
												ref={provided.innerRef}
												className={` `}
											>
												<div
													style={{
														gridTemplateColumns: "1rem 5rem 1fr 1fr 1fr 3rem",
													}}
													className={`grid grid-cols-4 group
						${
							listRowIndex === 0
								? "text-sm bg-gray-700 rounded "
								: "border-b  border-gray-800"
						}
						${
							selectedRow === listRowIndex
								? "bg-gray-800"
								: listRowIndex > 0 && "bg-gray-900"
						}
                                        `}
												>
													<span className="mx-auto my-auto text-gray-600 group-hover:text-gray-300">
														{listRowIndex !== 0 ? (
															<span className="flex flex-col items-center justify-around leading-none">
																<span className="text-xs">{listRowIndex}</span>{" "}
																=
															</span>
														) : (
															""
														)}
													</span>
													{listRowIndex === 0 && (
														<span className="px-2 py-2 block text-center">
															Selected
														</span>
													)}
													{listRowIndex > 0 && (
														<button
															onClick={() => {
																console.log(
																	"button clicked",
																	projectId,
																	listIndex,
																	listRowIndex
																);
																return cuesApi.updateDynamicListSelectedRow(
																	projectId,
																	listIndex,
																	listRowIndex
																);
															}}
															id={`button_${listIndex}_${listRowIndex}`}
															key={`button_${listIndex}_${listRowIndex}`}
															className={`px-2 py-2 block focus:outline-none focus:ring ring-main-600 rounded transition-opacity bg-gray-800 focus:z-20
                                                
													${
														selectedRow === listRowIndex
															? "opacity-100"
															: "opacity-0 hover:opacity-30 focus:opacity-90"
													}
                                                `}
														>
															👉
														</button>
													)}
													{row.map((r, index) => {
														if (r.value === "Selected") return null;
														return (
															<CellInput
																key={`list_call_${listRowIndex}_${index}`}
																listIndex={listIndex}
																cellIndex={index}
																rowIndex={listRowIndex}
																selectedRow={selectedRow}
																cell={r}
															/>
														);
													})}
													{listRowIndex > 0 && (
														<H2RButton
															icon={faClose}
															text=""
															colour="light"
															css="opacity-10 group-hover:opacity-100 focus:opacity-100"
															onClick={() => {
																return cuesApi.updateDynamicListRemoveRow(
																	projectId,
																	listIndex,
																	listRowIndex
																);
															}}
														/>
													)}
												</div>
											</div>
										)}
									</Draggable>
								);
							})}
						</div>
					)}
				</Droppable>
			</DragDropContext>
			<div className="mt-2 flex justify-between">
				<H2RButton
					text="Add row"
					size="small"
					icon={faPlus}
					colour="light"
					type="link"
					css="w-min"
					onClick={() => {
						return cuesApi.updateDynamicListAddRow(projectId, listIndex);
					}}
				/>
				{
					deleteList ? 
					<div className="flex gap-2">
						<H2RButton
						text="Cancel"
						size="small"
						colour="light"
						type="link"
						css="w-min"
						onClick={() => {
							setDeleteList(false)
						}}
					/>
						<H2RButton
						text="Yes, Delete list"
						size="small"
						icon={faTrash}
						colour="light"
						type="link"
						css="w-min"
						onClick={() => {
							return cuesApi.removeDynamicList(projectId, listIndex);
						}}
					/>
				</div>
				:
				<H2RButton
					text="Delete list"
					size="small"
					icon={faTrash}
					colour="light"
					type="link"
					css="w-min"
					onClick={() => {
						setDeleteList(true)
					}}
				/>
				}
				<H2RButton
					text="Import CSV"
					size="small"
					icon={faFileCsv}
					colour="light"
					type="link"
					css="w-min"
					onClick={() => {
						return socketActions.importCSV(projectId, listIndex);
					}}
				/>
			</div>
			<p className="text-xxs text-right text-gray-600">Importing will overwrite all data</p>
		</>
	);
};

const CellInput = ({ listIndex, cellIndex, rowIndex, selectedRow, cell }) => {
	let { projectId } = useParams();

	const [v, setV] = useState(cell.value);

	useEffect(() => {
		setV(cell.value);
	}, [cell.value]);

	return (
		<input
			key={`cell_${cellIndex}`}
			className={`px-2 py-2 w-full bg-transparent hover:bg-gray-800 focus:outline-none focus:ring ring-main-600 rounded focus-within:z-20
									                    ${selectedRow === rowIndex ? "font-bold" : "font-normal"}
									                    `}
			type="text"
			value={v}
			onChange={(e) => {
				let encodedV = e.target.value
				setV(e.target.value);
				return cuesApi.updateDynamicListCell(
					projectId,
					listIndex,
					rowIndex,
					cellIndex,
					encodedV
				);
			}}
		/>
	);
};
